import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { DownloadIcon } from "components/icons/Download";
import { Preview, PreviewBody } from "components/PreviewModal";
import { TASK_REVIEW_AND_PAY, TASK_REVIEW_DOCUMENT } from "constants/chatType";
import { TASK_AMENDMENT_DOCUMENTS } from "constants/taskStates";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGetPreviewUrlQuery } from "store/apis/previewUrl";
import {
  useGetTaskQuery,
  useMarkDocumentAsReviewMutation,
} from "store/apis/task";
import {
  closeDocumentReviewModal,
  openBalancingPaymentModal,
  setCustomData,
  setDocumentForReview,
} from "store/slices/reviewAndBalancePayment";
import { RootState } from "store/store";
import { useChatContext } from "stream-chat-react";
import { FileIcon } from "components/design/FileIcon";
import { openDownloadUrl } from "util/openDownloadUrl";
import { Channel } from "../Channel";
import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { FullScreenIcon } from "components/icons/FullScreenIcon";
import { useModal } from "hooks/useModal";
import { ConfirmReview } from "./ConfirmReview";
import { ReviewLink } from "types/Models/reviewDocumentAndBalancingPayment";

const DocumentSidebar = ({
  setCurrentFileNum,
  currentFile,
  selectedLink,
  onLinkClick,
}: {
  setCurrentFileNum: (index: number) => void;
  currentFile: number;
  selectedLink: ReviewLink | null;
  onLinkClick: (link: ReviewLink) => void;
}) => {
  const { documentForReview, customData: sidebarCustomData } = useSelector(
    (state: RootState) => state.reviewAndBalancePayment
  );
  const files = documentForReview ?? [];
  const links =
    sidebarCustomData?.ctas?.[0]?.metadata?.links ||
    sidebarCustomData?.metadata?.links ||
    [];

  const reviewedFilesCount = files.filter((file) => file.is_reviewed).length;

  return (
    <div className="t-w-full t-max-h-32 t-shrink-0 t-overflow-y-auto t-border-b md:t-w-fit md:t-max-h-none md:t-overflow-visible md:t-border-b-0 md:t-border-r t-bg-surface t-m-0 t-border-0 t-border-solid t-border-neutral-10">
      {files.length > 0 && (
        <p className="t-m-0 t-text-text-30 t-text-subtext-sm t-px-4 t-py-2">
          {reviewedFilesCount}/{files.length} Reviewed
        </p>
      )}
      {files.map((item, index) => (
        <div
          key={item.uuid}
          onClick={() => setCurrentFileNum(index)}
          className={classNames(
            "t-p-4 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-cursor-pointer t-capitalize",
            {
              "t-bg-purple-0 t-border-l t-border-l-purple":
                currentFile === index && !selectedLink,
            }
          )}
        >
          <p className="t-text-text-60 t-m-0 t-text-nowrap t-text-subtext-sm t-overflow-hidden t-text-ellipsis t-w-full md:t-w-32">
            {item.name}
          </p>
          <div className="t-text-body-sm t-flex t-items-center t-gap-x-1 t-text-text-60 t-m-0 t-mt-2">
            <div
              className={classNames("t-h-1.5 t-w-1.5 t-rounded-full", {
                "t-bg-orange-70": !item.is_reviewed,
                "t-bg-dark_green": item.is_reviewed,
              })}
            />
            {item.is_reviewed ? "Reviewed" : "Not Reviewed"}
          </div>
        </div>
      ))}
      {links.map((link) => {
        const isReviewed = Boolean(link.is_reviewed);
        return (
          <div
            key={link.url}
            onClick={() => onLinkClick(link)}
            className={classNames(
              "t-p-4 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-cursor-pointer t-capitalize",
              {
                "t-bg-purple-0 t-border-l t-border-l-purple":
                  selectedLink?.url === link.url,
              }
            )}
          >
            <p className="t-text-text-60 t-m-0 t-text-nowrap t-text-subtext-sm t-overflow-hidden t-text-ellipsis t-w-full md:t-w-32">
              {link.name || link.url}
            </p>
            <div className="t-text-body-sm t-flex t-items-center t-gap-x-1 t-text-text-60 t-m-0 t-mt-2">
              <div
                className={classNames("t-h-1.5 t-w-1.5 t-rounded-full", {
                  "t-bg-orange-70": !isReviewed,
                  "t-bg-dark_green": isReviewed,
                })}
              />
              {isReviewed ? "Reviewed" : "Not Reviewed"}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const DocumentReview = () => {
  const {
    activeChannelGroupId,
    documentForReview: filesRaw,
    documentOperationType,
    showDocumentReviewModal,
    isDocReviewed,
    isPaymentRequired,
    amount,
    documentForReviewMessageId,
    reviewChannelId,
    reviewTaskId,
    customData,
  } = useSelector((state: RootState) => state.reviewAndBalancePayment);

  const channelTaskId = useSelector(
    (state: RootState) => state.chat.channelTaskId
  );
  const { uuid: groupId } = useCurrentChatGroupContext();
  const files = filesRaw ?? [];
  const isLinkOnly = files.length === 0;
  const links: ReviewLink[] =
    customData?.ctas?.[0]?.metadata?.links || customData?.metadata?.links || [];
  const [currentFileNum, setCurrentFileNum] = useState(0);
  const [selectedLink, setSelectedLink] = useState<ReviewLink | null>(null);
  const reviewedLinkUrls = new Set(
    links.filter((l) => l.is_reviewed).map((l) => l.url)
  );

  const handleFileSelect = (index: number) => {
    setCurrentFileNum(index);
    setSelectedLink(null);
  };

  const handleLinkClick = (link: ReviewLink) => setSelectedLink(link);
  const currentFile = files[currentFileNum];
  const { data, isLoading, isSuccess } = useGetPreviewUrlQuery(
    {
      groupId: groupId || (activeChannelGroupId as string),
      fileId: currentFile?.uuid,
    },
    { skip: !Boolean(currentFile?.uuid) || (!activeChannelGroupId && !groupId) }
  );
  const { taskId } = useParams<{ taskId: string }>();
  const { data: task } = useGetTaskQuery(
    {
      taskId: taskId || channelTaskId || reviewTaskId,
    },
    { refetchOnMountOrArgChange: true }
  );
  const previewModal = useModal();
  const confirmModal = useModal();

  useEffect(() => {
    if (showDocumentReviewModal && isLinkOnly && links.length > 0) {
      setSelectedLink(links[0]);
    }
  }, [showDocumentReviewModal]);

  const { preview_url, download_url } = data || {};
  const { channel: currentChannel } = useChatContext();
  const channelUrl =
    typeof task?.channel === "string"
      ? task?.channel
      : task?.channel?.channel_url;

  const channelId = reviewChannelId || channelUrl || currentChannel?.id;
  const dispatch = useDispatch();
  const [documentMarkAsReview, { isLoading: isBtnLoading }] =
    useMarkDocumentAsReviewMutation();
  const { alertToast, successToast } = useToast();

  const openConfirmModal = async () => {
    if (
      documentOperationType === TASK_REVIEW_AND_PAY ||
      documentOperationType === TASK_AMENDMENT_DOCUMENTS
    ) {
      await markDocumentAsReview();
      if (isPaymentRequired && task) {
        dispatch(openBalancingPaymentModal(task?.entity.uuid));
      }
    }
    if (documentOperationType === TASK_REVIEW_DOCUMENT) {
      confirmModal.open();
    }
  };

  const markDocumentAsReview = async () => {
    try {
      const documentIds = selectedLink ? "" : currentFile?.uuid ?? "";
      const linkIds = selectedLink?.task_link_map_id
        ? [selectedLink.task_link_map_id]
        : undefined;

      await documentMarkAsReview({
        channel_id: channelId!,
        group_id: groupId || activeChannelGroupId,
        task_id: channelTaskId || taskId || reviewTaskId,
        message_id: documentForReviewMessageId,
        document_ids: documentIds,
        link_ids: linkIds,
      }).unwrap();

      if (selectedLink) {
        const updatedLinks = links.map((l) =>
          l.url === selectedLink.url ? { ...l, is_reviewed: true } : l
        );
        if (customData) {
          const updatedCtas = customData.ctas?.map((cta, i) =>
            i === 0
              ? { ...cta, metadata: { ...cta.metadata, links: updatedLinks } }
              : cta
          );
          dispatch(setCustomData({ ...customData, ctas: updatedCtas }));
        }

        const nextUnreviewedLink = updatedLinks.find((l) => !l.is_reviewed);
        if (nextUnreviewedLink) {
          setSelectedLink(nextUnreviewedLink);
        } else if (isLinkOnly) {
          dispatch(closeDocumentReviewModal());
        } else {
          const firstUnreviewedFileIdx = files.findIndex((f) => !f.is_reviewed);
          setSelectedLink(null);
          setCurrentFileNum(
            firstUnreviewedFileIdx >= 0 ? firstUnreviewedFileIdx : 0
          );
        }
      } else {
        const updatedFiles = files.map((file) => {
          if (file.uuid === documentIds) {
            return { ...file, is_reviewed: true };
          }
          return file;
        });

        dispatch(setDocumentForReview(updatedFiles));

        if (
          files.length > 1 &&
          files[currentFileNum + 1] &&
          !files[currentFileNum + 1].is_reviewed
        ) {
          setCurrentFileNum(currentFileNum + 1);
        }
      }

      successToast({ message: "Document marked as reviewed" });
    } catch (error: any) {
      alertToast({ message: error?.response?.data?.error?.message });
    } finally {
      confirmModal.close();
    }
  };

  const isLinkReviewed = Boolean(
    selectedLink && reviewedLinkUrls.has(selectedLink.url)
  );

  const isMarkReviewDisabled = Boolean(
    isBtnLoading ||
      isDocReviewed ||
      (selectedLink ? isLinkReviewed : currentFile?.is_reviewed)
  );

  const noPaymentButtonText = isMarkReviewDisabled
    ? "Marked as Reviewed"
    : "Mark as reviewed";

  return (
    <>
      <Modal.Root
        open={showDocumentReviewModal}
        onOpenChange={() => dispatch(closeDocumentReviewModal())}
      >
        <Modal.Content size="xxxl" className="t-rounded-md">
          <Modal.Header className="!t-px-4">
            <Modal.Title>Review documents</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-h-[80dvh] md:t-h-[651px] t-rounded-b-md t-overflow-hidden t-bg-surface-lighter-grey t-flex t-flex-col md:t-flex-row !t-px-0 !t-py-0">
            <DocumentSidebar
              setCurrentFileNum={handleFileSelect}
              currentFile={currentFileNum}
              selectedLink={selectedLink}
              onLinkClick={handleLinkClick}
            />
            <div className="t-flex t-min-h-0 t-flex-col t-h-full t-flex-grow t-gap-0 t-m-0">
              <div className="t-min-h-0 t-flex-grow t-pt-4 t-overflow-hidden t-h-full">
                {selectedLink ? (
                  <div className="t-w-full t-h-full t-flex t-items-center t-justify-center">
                    <div className="t-rounded-lg t-px-6 t-py-4 t-bg-neutral-80 t-text-white t-text-body-sm">
                      <a
                        href={selectedLink.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="t-text-blue-50 hover:t-text-blue"
                      >
                        click here
                      </a>{" "}
                      to see the document.
                    </div>
                  </div>
                ) : !isLinkOnly ? (
                  <Async.Root
                    isSuccess={isSuccess}
                    isLoading={isLoading}
                    isEmpty={files.length === 0 && !isLoading}
                    customLoader={
                      <div className="t-w-full t-px-6">
                        <Loader />
                      </div>
                    }
                  >
                    <Async.Success>
                      {currentFile && (
                        <div className="t-flex t-flex-col t-gap-4 t-h-full">
                          <div className="t-w-full t-h-6 t-shrink-0 t-flex t-justify-between t-items-center t-px-3 md:t-px-6">
                            <div className="t-flex t-min-w-0 t-gap-4 t-items-center">
                              <FileIcon
                                fileType={currentFile.file_type}
                                size="24"
                              />
                              <div className="t-min-w-0 t-truncate t-text-body-sm t-text-text-100">
                                {currentFile.name}
                              </div>
                              <Button
                                customType="ghost"
                                size="small"
                                onClick={() => openDownloadUrl(download_url!)}
                              >
                                <span className="t-text-text-30">
                                  <DownloadIcon
                                    color="currentColor"
                                    strokeWidth="1.5"
                                  />
                                </span>
                              </Button>
                            </div>
                            <Button
                              customType="ghost"
                              onClick={previewModal.open}
                            >
                              <div className="t-text-text-30">
                                <FullScreenIcon />
                              </div>
                            </Button>
                          </div>
                          <div className="t-w-full t-min-h-0 t-px-3 md:t-px-6 t-h-full t-flex t-relative t-overflow-hidden t-items-center t-justify-center t-gap-4">
                            <PreviewBody
                              file={currentFile}
                              preview_url={preview_url!}
                              fullWidth
                            />
                          </div>
                        </div>
                      )}
                    </Async.Success>
                  </Async.Root>
                ) : null}
              </div>

              <div>
                <div className="t-hidden t-bg-surface t-h-16 t-w-full md:t-block" />
                <div className="t-flex t-w-full t-box-border t-px-4 t-justify-end t-gap-3 t-py-4 t-bg-surface t-border-0 t-border-t t-border-solid t-border-neutral-10 md:t-px-6">
                  <Button
                    type="button"
                    onClick={() => dispatch(closeDocumentReviewModal())}
                  >
                    Cancel
                  </Button>
                  <Button
                    customType="primary"
                    className="t-flex-1 md:t-flex-none"
                    isLoading={isBtnLoading}
                    disabled={isMarkReviewDisabled}
                    onClick={openConfirmModal}
                  >
                    {documentOperationType === TASK_REVIEW_AND_PAY &&
                    isPaymentRequired
                      ? `Mark as reviewed and pay $${amount}`
                      : noPaymentButtonText}
                  </Button>
                </div>
              </div>
            </div>
            <div className="t-hidden t-w-[360px] t-shrink-0 md:t-block">
              <Channel
                channelId={channelId}
                Header={() => (
                  <div className="t-w-full t-h-12 t-bg-surface t-p-4 t-text-subtitle">
                    Chat
                  </div>
                )}
                messageMaxWidth="360px"
              />
            </div>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
      {previewModal.isOpen && (
        <Preview
          showModal={previewModal.isOpen}
          closeModal={previewModal.close}
          groupId={groupId}
          previewId={currentFile?.uuid}
        />
      )}
      {confirmModal.isOpen && (
        <ConfirmReview
          show={confirmModal.isOpen}
          text="Are you sure you have reviewed the document?"
          closeModal={confirmModal.close}
          isLoading={isBtnLoading}
          markDocumentAsReview={markDocumentAsReview}
        />
      )}
    </>
  );
};
